import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { ArrowLeft, Calendar, ChevronLeft, ChevronRight, X, BellRing } from 'lucide-react';
import { toast } from 'sonner';
import HistoricalEarningsCard from '@/components/HistoricalEarningsCard';
import { usePortfolioScopeParams } from '@/lib/portfolioScopeParams';
import CalendarScopeToggle from '@/components/CalendarScopeToggle';
import ExDivAlertSettings from '@/components/ExDivAlertSettings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function MonthPill({ month, label, count, amount, isSelected, onClick }) {
  // Tiered color based on number of pay dates
  let bgClass;
  if (count === 0) {
    bgClass = 'bg-muted text-muted-foreground hover:bg-muted/80';
  } else if (count <= 2) {
    bgClass = 'bg-teal-300/70 dark:bg-teal-600/50 text-teal-900 dark:text-teal-100 hover:bg-teal-300 dark:hover:bg-teal-600/70';
  } else if (count <= 5) {
    bgClass = 'bg-teal-400 dark:bg-teal-500/70 text-white hover:bg-teal-500 dark:hover:bg-teal-500';
  } else if (count <= 10) {
    bgClass = 'bg-teal-600 dark:bg-teal-500 text-white hover:bg-teal-700 dark:hover:bg-teal-400';
  } else {
    bgClass = 'bg-green-700 dark:bg-green-600 text-white hover:bg-green-800 dark:hover:bg-green-500';
  }

  // Format amount: $123 or $1.2k
  const formatAmount = (amt) => {
    if (!amt || amt <= 0) return null;
    if (amt >= 1000) return `$${(amt / 1000).toFixed(1)}k`;
    return `$${Math.round(amt)}`;
  };
  const amtLabel = formatAmount(amount);

  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center justify-center rounded-lg px-2 py-1.5 min-w-[60px] transition-all text-xs font-semibold border-2
        ${isSelected
          ? `${bgClass} border-blue-500 ring-2 ring-blue-500 ring-offset-1 ring-offset-background`
          : `${bgClass} border-blue-400/50 dark:border-blue-500/40`
        }`}
      data-testid={`calendar-month-pill-${month}`}
    >
      <span>{label}</span>
      <span className="text-[10px] leading-tight mt-0.5">{count > 0 ? `${count} pay` : '—'}</span>
      {amtLabel && <span className="text-[10px] leading-tight font-bold">{amtLabel}</span>}
    </button>
  );
}

function CalendarGrid({ year, month, events, today, onStockClick }) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const [openDate, setOpenDate] = useState(null);

  // Close modal on Escape key
  useEffect(() => {
    if (!openDate) return;
    const handleKey = (e) => { if (e.key === 'Escape') setOpenDate(null); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [openDate]);

  // Close modal whenever the displayed month changes
  useEffect(() => { setOpenDate(null); }, [year, month]);

  const cells = [];
  // Empty cells before first day
  for (let i = 0; i < firstDay; i++) {
    cells.push({ day: null, events: [] });
  }
  // Days of month
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dayEvents = events.filter(e => e.date === dateStr);
    cells.push({ day: d, dateStr, events: dayEvents, isToday: dateStr === todayStr });
  }

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  // Pad last week
  const lastWeek = weeks[weeks.length - 1];
  while (lastWeek.length < 7) {
    lastWeek.push({ day: null, events: [] });
  }

  const renderEventPill = (ev, ei) => {
    const isPortfolio = ev.source === 'portfolio';
    const borderColor = isPortfolio
      ? 'border-l-blue-500 dark:border-l-blue-400'
      : 'border-l-purple-500 dark:border-l-purple-400';
    return (
      <div
        key={ei}
        className={`text-[9px] font-semibold px-1 py-0 rounded truncate leading-tight border-l-2 ${borderColor}
          ${ev.type === 'ex_div'
            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
            : 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
          }`}
        data-testid={`calendar-event-${ev.ticker}-${ev.type}`}
      >
        {ev.type === 'ex_div'
          ? <span className="inline-block w-1 h-1 rounded-full mr-1 bg-amber-500" />
          : <span className="inline-block mr-1 font-bold text-green-700 dark:text-green-400 leading-none text-[10px]" aria-label="Pay date">$</span>
        }
        {ev.label}
      </div>
    );
  };

  return (
    <div className="rounded-xl border bg-card overflow-visible relative" data-testid="calendar-grid">
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b">
        {DAY_NAMES.map(d => (
          <div key={d} className="text-center text-xs font-semibold text-muted-foreground py-2.5">{d}</div>
        ))}
      </div>
      {/* Weeks */}
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7 border-b last:border-b-0">
          {week.map((cell, ci) => (
            <button
              key={ci}
              type="button"
              onClick={() => { if (cell.day) setOpenDate(cell.dateStr); }}
              disabled={!cell.day}
              className={`min-h-[56px] sm:min-h-[64px] p-1 border-r last:border-r-0 text-left transition-colors
                ${!cell.day ? 'bg-muted/20 cursor-default' : 'hover:bg-muted/30 cursor-pointer'}`}
              data-testid={cell.dateStr ? `calendar-day-${cell.dateStr}` : undefined}
            >
              {cell.day && (
                <>
                  {/* Day number with black outline circle; today stays blue */}
                  <span className={`text-[11px] font-medium inline-flex items-center justify-center w-5 h-5 rounded-full border
                    ${cell.isToday
                      ? 'bg-blue-600 text-white border-foreground'
                      : 'text-foreground border-foreground/70'}`}>
                    {cell.day}
                  </span>
                  <div className="mt-0.5 space-y-0.5">
                    {/* Show only first event in the cell */}
                    {cell.events.slice(0, 1).map((ev, ei) => renderEventPill(ev, ei))}
                    {cell.events.length > 1 && (
                      <span className="w-full inline-flex items-center justify-center gap-0.5 text-[9px] font-bold text-muted-foreground bg-muted/40 rounded px-1 py-0 leading-tight">
                        +{cell.events.length - 1} more
                      </span>
                    )}
                  </div>
                </>
              )}
            </button>
          ))}
        </div>
      ))}

      {/* Day details modal — centered, no swipe, click outside closes */}
      {openDate && (
        <DayDetailsModal
          dateStr={openDate}
          events={events.filter(e => e.date === openDate)}
          onClose={() => setOpenDate(null)}
          onStockClick={onStockClick}
        />
      )}
    </div>
  );
}

function DayDetailsModal({ dateStr, events, onClose, onStockClick }) {
  // Parse YYYY-MM-DD → full long-form date
  const [y, m, d] = dateStr.split('-').map(Number);
  const dateObj = new Date(y, m - 1, d);
  const fullLabel = dateObj.toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  // Group events by ticker so we can show ex-div + payment on the same line
  const grouped = {};
  for (const ev of events) {
    if (!grouped[ev.ticker]) grouped[ev.ticker] = {
      ticker: ev.ticker, name: ev.name,
      ex_div: null, payment: null,
      perShare: null, total: null, shares: null,
      ineligibleShares: 0,
      ex_confirmed: false, pay_confirmed: false,
      source: ev.source || null,
    };
    if (ev.source) grouped[ev.ticker].source = ev.source;
    if (ev.type === 'ex_div') {
      grouped[ev.ticker].ex_div = ev;
      grouped[ev.ticker].ex_confirmed = !!ev.confirmed;
    }
    if (ev.type === 'pay_date' || ev.type === 'payment') {
      grouped[ev.ticker].payment = ev;
      grouped[ev.ticker].pay_confirmed = !!ev.confirmed;
    }
    if (ev.per_share != null) grouped[ev.ticker].perShare = ev.per_share;
    if (ev.amount != null && ev.type !== 'ex_div') grouped[ev.ticker].total = ev.amount;
    if (ev.shares != null) grouped[ev.ticker].shares = ev.shares;
    // Round 92o++++++++N+++++++++++ (Feb 2026) — capture
    // per-event ineligibility. The ex_div and pay_date events for
    // a given cycle carry the same `ineligible_shares` count; we
    // take the max so a stray missing field on one of them doesn't
    // silently zero the row's chip.
    if (ev.ineligible_shares != null && ev.ineligible_shares > grouped[ev.ticker].ineligibleShares) {
      grouped[ev.ticker].ineligibleShares = ev.ineligible_shares;
    }
  }
  for (const k in grouped) {
    const r = grouped[k];
    if (r.perShare == null && r.total != null && r.shares != null && r.shares > 0) {
      r.perShare = r.total / r.shares;
    }
  }
  const rows = Object.values(grouped);

  const fmt$ = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  // Round 93r (Feb 2026, per user request) — per-share dividend amount in the
  // day-details modal now renders at 4 decimal places using standard
  // "round half up" (5+ rounds up). Strips trailing zeros for readability.
  const fmt$4 = (v) => {
    if (v == null) return '—';
    const n = Number(v);
    if (!isFinite(n)) return '—';
    const rounded = Math.round(n * 10000) / 10000;
    // Format with up to 4 decimals; trailing zeros stripped but ensure
    // at least 2 decimals so "$0.41" doesn't drop to "$0.41" (it stays).
    const formatted = rounded.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    return `$${formatted}`;
  };
  // Round 93r — shares column: when fractional, clamp to 4 decimals using
  // "round half up". Whole-number share counts render plain (no ".0000").
  const fmtShares4 = (v) => {
    if (v == null) return null;
    const n = Number(v);
    if (!isFinite(n)) return null;
    if (Number.isInteger(n)) return n.toLocaleString();
    const rounded = Math.round(n * 10000) / 10000;
    return rounded.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 4 });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3"
      onClick={onClose}
      data-testid={`calendar-day-modal-${dateStr}`}
    >
      <div
        className="bg-card border rounded-xl shadow-2xl w-full max-w-md relative max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header: full date + red X */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-sm sm:text-base font-bold" data-testid="calendar-day-modal-title">{fullLabel}</h3>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors"
            aria-label="Close"
            data-testid="calendar-day-modal-close"
          >
            <X className="w-3.5 h-3.5" strokeWidth={3} />
          </button>
        </div>

        {/* Event list */}
        <div className="p-3 space-y-2 overflow-y-auto">
          {rows.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No dividend events on this date.</p>
          ) : rows.map((r, i) => (
            <div key={i} className="border rounded-lg p-2 space-y-1.5" data-testid={`calendar-day-modal-row-${r.ticker}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  {onStockClick ? (
                    <button
                      type="button"
                      onClick={() => { onClose?.(); onStockClick(r.ticker); }}
                      className="font-mono text-xs font-bold text-yellow-600 dark:text-yellow-400 hover:underline focus:outline-none focus:ring-2 focus:ring-yellow-500/40 rounded-sm"
                      title={`Open ${r.ticker} stock details`}
                      data-testid={`calendar-day-modal-ticker-${r.ticker}`}
                    >
                      {r.ticker}
                    </button>
                  ) : (
                    <span className="font-mono text-xs font-bold text-yellow-600 dark:text-yellow-400">{r.ticker}</span>
                  )}
                  {r.source && (
                    <span
                      className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded
                        ${r.source === 'portfolio'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                          : 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'
                        }`}
                      data-testid={`calendar-day-modal-source-${r.ticker}`}
                    >
                      {r.source === 'portfolio' ? 'Portfolio' : 'Watchlist'}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground truncate">{r.name || ''}</span>
                </div>
                {r.payment && (
                  <div className="flex items-center gap-3 text-[11px] font-mono">
                    <span>
                      <span className="text-muted-foreground mr-1">/sh</span>
                      <span className="font-semibold">{fmt$4(r.perShare)}</span>
                    </span>
                    <span>
                      <span className="text-muted-foreground mr-1">Total</span>
                      <span className="font-bold text-green-700 dark:text-green-400">{fmt$(r.total)}</span>
                    </span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-1.5 flex-wrap">
                {r.ex_div && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    Ex-Div
                    <span className="ml-1 font-bold uppercase tracking-wide text-[9px]">
                      {r.ex_confirmed ? 'Upcoming' : 'Estimated'}
                    </span>
                  </span>
                )}
                {r.payment && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                    <span className="font-bold text-green-700 dark:text-green-400 leading-none" aria-label="Pay date">$</span>
                    Payment
                    <span className="ml-1 font-bold uppercase tracking-wide text-[9px]">
                      {r.pay_confirmed ? 'Upcoming' : 'Estimated'}
                    </span>
                  </span>
                )}
                {r.shares != null && (
                  <span className="text-[10px] text-muted-foreground font-mono ml-auto whitespace-nowrap">{fmtShares4(r.shares)} shares</span>
                )}
                {/* Round 92o++++++++N+++++++++++ (Feb 2026) — per-row
                    ex-div ineligibility chip. `basis-full` on phone
                    so it claims its own line below the badges/share
                    count and never crowds them; `sm:basis-auto` on
                    tablet+ lets it sit inline. Self-hides when 0. */}
                {r.ineligibleShares > 0 && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded
                               bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300
                               basis-full sm:basis-auto whitespace-nowrap"
                    title="These lots were purchased on or after this cycle's ex-dividend date and aren't entitled to this payment. They'll start collecting on the next cycle."
                    data-testid={`calendar-day-modal-ineligible-${r.ticker}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    {Number.isInteger(r.ineligibleShares)
                      ? r.ineligibleShares
                      : Number(r.ineligibleShares).toFixed(3).replace(/\.?0+$/, '')}
                    {' not yet eligible'}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function DividendCalendar({ onBack, isDark, onStockClick }) {
  const [events, setEvents] = useState([]);
  const [trackedStocks, setTrackedStocks] = useState(0);
  const [annualDividends, setAnnualDividends] = useState(0);
  const [loading, setLoading] = useState(true);
  const today = new Date();
  const currentYear = today.getFullYear();
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth());
  const [displayMonth, setDisplayMonth] = useState(today.getMonth());
  // Round 93r — Ex-Div Alert settings panel collapsible state.
  const [alertSettingsOpen, setAlertSettingsOpen] = useState(false);
  // Round 92o++++++++N++++++++++ (Jun 2026) — scope params: active
  // portfolio + (optional) include_watchlist=false. Shared store
  // keeps this toggle in sync with the Dashboard 12-month heatmap.
  const scopeParams = usePortfolioScopeParams({ withWatchlistFlag: true });
  const scopeKey = `${scopeParams.portfolio_id || 'all'}|${scopeParams.include_watchlist === false ? 'po' : 'both'}`;

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/portfolio/dividend-calendar`, {
      params: { year: currentYear, ...scopeParams },
      withCredentials: true
    })
      .then(res => {
        setEvents(res.data.events || []);
        setTrackedStocks(res.data.tracked_stocks || 0);
        setAnnualDividends(res.data.annual_dividends || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentYear, scopeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Month PAY DATE counts + $ amounts
  const monthCounts = useMemo(() => {
    const counts = Array(12).fill(0);
    events.forEach(e => {
      if (e.type === 'pay_date') {
        const m = parseInt(e.date.split('-')[1], 10) - 1;
        if (m >= 0 && m < 12) counts[m]++;
      }
    });
    return counts;
  }, [events]);

  const monthAmounts = useMemo(() => {
    const amts = Array(12).fill(0);
    events.forEach(e => {
      if (e.type === 'pay_date' && e.amount) {
        const m = parseInt(e.date.split('-')[1], 10) - 1;
        if (m >= 0 && m < 12) amts[m] += e.amount;
      }
    });
    return amts;
  }, [events]);

  // Monthly financial summary
  const monthSummary = useMemo(() => {
    const monthEvents = events.filter(e => {
      const m = parseInt(e.date.split('-')[1], 10) - 1;
      return m === selectedMonth;
    });
    const payEvents = monthEvents.filter(e => e.type === 'pay_date');
    const estCash = payEvents.reduce((sum, e) => sum + (e.amount || 0), 0);
    return {
      payments: payEvents.length,
      estCash: estCash,
    };
  }, [events, selectedMonth]);

  const handleMonthClick = (m) => {
    setSelectedMonth(m);
    setDisplayMonth(m);
  };

  const prevMonth = () => {
    const m = displayMonth > 0 ? displayMonth - 1 : 11;
    setDisplayMonth(m);
    setSelectedMonth(m);
  };

  const nextMonth = () => {
    const m = displayMonth < 11 ? displayMonth + 1 : 0;
    setDisplayMonth(m);
    setSelectedMonth(m);
  };

  const displayEvents = events.filter(e => {
    const m = parseInt(e.date.split('-')[1], 10) - 1;
    return m === displayMonth;
  });

  return (
    <div className="space-y-6 w-full max-w-full overflow-x-hidden" data-testid="dividend-calendar-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-center gap-2">
        <button onClick={onBack} className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1" data-testid="calendar-back-btn">
          <ArrowLeft className="w-3.5 h-3.5" />
          My Portfolio
        </button>
        <span className="text-muted-foreground text-xs">/</span>
        {/* Round 93r — title icon + text bumped to stand out from the
            sibling back-button (left) and scope-toggle pill (right) so
            page identity is the dominant focal point. */}
        <div className="flex items-center gap-2">
          <Calendar className="w-7 h-7 text-primary" />
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Dividend Calendar</h1>
        </div>
        {/* Round 92o++++++++N++++++++++ (Jun 2026) — scope toggle.
            Flips between "Portfolio only" and "Portfolio + Watchlist".
            Shares state with the Dashboard 12-month heatmap toggle so
            both surfaces stay in lockstep. */}
        <div className="ml-2">
          <CalendarScopeToggle size="sm" testIdPrefix="calendar-scope" />
        </div>
        {/* Round 93r — Ex-Div Alert pop-up settings. Toggles a panel
            that governs which lead-time windows (5d / 3d / 1d) trigger
            the dashboard pop-up modal on first login each day. */}
        <button
          type="button"
          onClick={() => setAlertSettingsOpen((v) => !v)}
          className={`ml-1 inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-md border transition-colors ${
            alertSettingsOpen
              ? 'border-violet-500 bg-violet-600 text-white hover:bg-violet-700'
              : 'border-violet-300 dark:border-violet-700 bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/40'
          }`}
          data-testid="calendar-alerts-settings-toggle"
          title="Configure ex-dividend alert pop-ups"
        >
          <BellRing className="w-3.5 h-3.5" />
          Alerts
        </button>
      </div>

      {/* Round 93r — Alert settings disclosure panel (collapsed by default). */}
      {alertSettingsOpen && (
        <div className="rounded-xl border-2 border-violet-500/30 bg-card p-4 max-w-xl mx-auto" data-testid="calendar-alerts-settings-panel">
          <ExDivAlertSettings
            onClose={() => setAlertSettingsOpen(false)}
            onStockClick={(ticker) => { setAlertSettingsOpen(false); onStockClick?.(ticker); }}
          />
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Annual Income Overview */}
          <div className="rounded-xl border bg-card p-4" data-testid="annual-income-overview">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div className="flex items-baseline gap-2 flex-wrap">
                <h2 className="text-sm font-bold">Annual Income Overview — {currentYear}</h2>
                <span className="text-xs text-muted-foreground">
                  Est.{' '}
                  <span className="text-sm font-bold text-blue-600 dark:text-blue-400 font-mono" data-testid="calendar-annual-total">
                    ${annualDividends.toFixed(2)}
                  </span>
                </span>
                <p className="text-xs text-muted-foreground basis-full">Identify gaps in your passive income stream</p>
              </div>
              {/* Right cluster — color legend right-aligned at the box
                  edge. Phone (when the parent flex-wraps): the cluster
                  drops to its own line below the title. */}
              <div className="flex flex-col items-end gap-1.5 ml-auto" data-testid="annual-income-right">
                {/*
                  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                  ICS Calendar Download — REMOVED Feb 2026 (Round 93s)
                  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                  User tried 3 different calendar apps (web + clients).
                  None of them imported the events despite the .ics
                  passing every RFC 5545 lint check we could run. After
                  staying gated under `{false && ...}` since May 2026,
                  removed the dead button code entirely — keeping it
                  was just visual noise. The backend
                  /api/portfolio/dividend-calendar.ics endpoint is
                  preserved for now (zero attack surface, may be
                  bookmarked by power users) but no longer surfaced
                  in the UI.
                  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                */}
                <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-muted inline-block" />No payments</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-teal-500 inline-block" />High income</span>
                </div>
              </div>
            </div>
            <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="month-pills">
              {MONTH_SHORT.map((label, i) => (
                <MonthPill
                  key={i}
                  month={i}
                  label={label}
                  count={monthCounts[i]}
                  amount={monthAmounts[i]}
                  isSelected={selectedMonth === i}
                  onClick={() => handleMonthClick(i)}
                />
              ))}
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-3" data-testid="calendar-summary-cards">
            <div className="rounded-xl border bg-card p-4">
              <p className="text-xs text-muted-foreground">Tracked Stocks</p>
              <p className="text-2xl font-bold mt-1" data-testid="calendar-tracked-stocks">{trackedStocks}</p>
              <p className="text-[10px] text-muted-foreground">{scopeParams.include_watchlist === false ? 'portfolio only' : 'portfolio + watchlist'}</p>
            </div>
            <div className="rounded-xl border bg-card p-4">
              <p className="text-xs text-muted-foreground">Est. Cash This Month</p>
              <p className="text-2xl font-bold mt-1 text-green-600 dark:text-green-400" data-testid="calendar-est-cash">
                ${monthSummary.estCash.toFixed(2)}
              </p>
              <p className="text-[10px] text-muted-foreground">from portfolio positions</p>
            </div>
            <div className="rounded-xl border bg-card p-4">
              <p className="text-xs text-muted-foreground">Payments This Month</p>
              <p className="text-2xl font-bold mt-1" data-testid="calendar-payments">{monthSummary.payments}</p>
              <p className="text-[10px] text-muted-foreground">pay date events</p>
            </div>
          </div>

          {/* Calendar Navigation */}
          <div className="flex items-center justify-between">
            <button onClick={prevMonth} className="p-2 rounded-lg hover:bg-muted transition-colors" data-testid="calendar-prev-month">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-bold">{MONTH_NAMES[displayMonth]} {currentYear}</h2>
            <button onClick={nextMonth} className="p-2 rounded-lg hover:bg-muted transition-colors" data-testid="calendar-next-month">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Legend (placed above grid so it's always visible without scrolling) */}
          <div className="flex items-center gap-4 sm:gap-6 justify-center text-xs flex-wrap" data-testid="calendar-legend">
            <span className="text-muted-foreground font-semibold">Key:</span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
              Ex-Div
            </span>
            <span className="flex items-center gap-1.5">
              <span className="font-bold text-green-700 dark:text-green-400 leading-none text-sm" aria-label="Pay date">$</span>
              Pay Date
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-0.5 h-3 bg-blue-500 dark:bg-blue-400" />
              Portfolio
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-0.5 h-3 bg-purple-500 dark:bg-purple-400" />
              Watchlist
            </span>
          </div>

          {/* Calendar Grid */}
          <CalendarGrid year={currentYear} month={displayMonth} events={displayEvents} today={today} onStockClick={onStockClick} />

          {/* Round 77b — Historical Earnings tracker. Renders only
              when the user actually has positions AND we have any
              cached historical data; the component handles its own
              empty-state suppression. */}
          <HistoricalEarningsCard />
        </>
      )}
    </div>
  );
}
